import React, { useMemo, useState } from 'react';
import { Drawer, Tag, Empty, Tooltip } from 'antd';
import {
    CheckCircleFilled,
    CloseCircleFilled,
    ExclamationCircleFilled,
    InfoCircleFilled,
    UpOutlined,
    DownOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import type { CellAlert } from '@liveline/ll_node_common/lib/models/websocket';
import { RootState } from '../../../state/features/reducers';
import { userSelectionActions } from '../../../state/features/userSelectionReducer';
import useCellAlerts, { isAlertVisible, isAlertRoleBlocked } from '../../../hooks/useCellAlerts';
import {
    AlertSensor,
    formatAlertMessage,
    messageAfterTag,
    resolveAlertSensor,
} from '../../../utils/alertSensor';
import { SIGNAL_DATA_PATH, SignalFocusRequest } from '../../monitor/signal-data/SignalFocusContext';
import ConfigProviderComponent from '../config-provider/ConfigProviderComponent';
import { antdTheme } from '../antdTheme';
import './alertFooterBar.scss';

const { CheckableTag } = Tag;

/* LT-3215 — display scope of the feed. 'line' = only the line currently
   selected in the console; 'all' = the facility-wide stream (the original,
   and the only behaviour when no line is selected). Subscription is
   unaffected: the socket always carries every cell of the facility. */
type AlertScope = 'line' | 'all';

/* Routes that are facility-wide BY DEFINITION — they show every line at once, so
   "this line only" has no meaning on them. userSelection.cell persists across
   navigation, so without this a scope chosen on a per-line page follows the
   operator onto the dashboard and blanks its alarm line: "Fortrex 1 only / no
   active alerts" while another line sits on 19 warnings. */
const FACILITY_WIDE_ROUTES = ['/dashboard'];

/* The scope choice survives in sessionStorage rather than component state alone.
   The footer is mounted once for the whole app, so plain state already survives
   navigation — but not a remount, and not a reload, and the operator reads a
   scope that silently reverts as a bug in the filter. sessionStorage (not local)
   keeps the "a fresh session opens facility-wide" rule: a scope is a decision
   about the shift you are working, not a permanent preference. */
const SCOPE_STORAGE_KEY = 'cellAlertsScope';

const readStoredScope = (): AlertScope | null => {
    try {
        const v = sessionStorage.getItem(SCOPE_STORAGE_KEY);
        return v === 'line' || v === 'all' ? v : null;
    } catch {
        return null; // private mode / storage disabled — fall back to the default
    }
};

const SEVERITY_ICON: Record<string, React.ReactNode> = {
    critical: <CloseCircleFilled style={{ color: '#f5222d' }} />,
    warning: <ExclamationCircleFilled style={{ color: '#faad14' }} />,
    info: <InfoCircleFilled style={{ color: '#006b8c' }} />,
};

// LT-3110 — a CLEARED message is a recovery, so it must not wear the severity
// glyph of the condition it retires: a producer whose clear text reads "part is
// present" rendered behind an amber warning triangle reads as a fresh fault
// (green matches the row's CLEARED state label).
const CLEARED_ICON: React.ReactNode = <CheckCircleFilled style={{ color: '#52c41a' }} />;

const alertIcon = (alert: CellAlert): React.ReactNode =>
    alert.state === 'cleared' ? CLEARED_ICON : SEVERITY_ICON[alert.severity] ?? SEVERITY_ICON.info;

/* LT-3193 — alerts outlive a shift, so a bare time-of-day can't tell yesterday's
   alert from this morning's. Month/day only: the feed is a live ring buffer, so
   nothing in it is old enough for the year to disambiguate anything. */
const formatTime = (ms: number): string => {
    const d = new Date(ms);
    if (Number.isNaN(d.getTime())) return '—';
    const date = d.toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' });
    return `${date}, ${d.toLocaleTimeString()}`;
};

/**
 * LT-3052 — the console "alarm line": a slim, always-present bar docked at the
 * bottom of the content area (mounted once in the private-routes layout) showing
 * severity counters + the most recent unresolved alert, expanding upward into a
 * bottom Drawer with the full live feed and audience filter chips.
 */
const AlertFooterBar: React.FC = () => {
    const { t } = useTranslation();
    const { alerts, facilityId, visibleAudiences, cellNames, tagCatalogue } = useCellAlerts();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const cellList = useSelector((s: RootState) => s.cellList?.data);
    const [expanded, setExpanded] = useState(false);
    const [audienceFilter, setAudienceFilter] = useState<string[] | null>(null); // null = defaults
    // The line the operator is looking at. cell.id IS the facility_line_id every
    // alert carries and the same id the DB-backed Alert Log page scopes by, so no
    // mapping is needed. userSelection.cell defaults to {} — hence the truthiness
    // guards below rather than a null check.
    const selectedCellId = useSelector((s: RootState) => s.userSelection?.cell?.id);
    // null = follow the default, which is the facility-wide stream: the footer is
    // the facility's alarm line, so it must not start out hiding another line's
    // fault. Scoping is an explicit operator choice, and it sticks once made.
    const [scopeOverride, setScopeOverride] = useState<AlertScope | null>(readStoredScope);
    // A facility-wide page always shows all lines. This overrides the operator's
    // choice for the duration of that page rather than clearing it, so returning
    // to a per-line page restores the scope they picked.
    const { pathname } = useLocation();
    const facilityWidePage = FACILITY_WIDE_ROUTES.some(
        (r) => pathname === r || pathname.startsWith(`${r}/`),
    );
    const scope: AlertScope = facilityWidePage ? 'all' : scopeOverride ?? 'all';
    const scoped = scope === 'line' && Boolean(selectedCellId);
    // Opening (or closing) the drawer marks everything currently received as SEEN;
    // the bar's count chips then record only alerts arriving after that.
    const [seenTs, setSeenTs] = useState<Record<number, number>>({});
    const markSeen = () => setSeenTs((prev) => ({ ...prev, [facilityId]: Date.now() }));

    /**
     * Name the cell the way the rest of the console does ("Line 5"), falling
     * back to "Cell <id>" when the alert names a cell that is not in the
     * current facility's cell list (stale ring-buffer entry, facility switch
     * mid-flight, or a cell added since the list was cached).
     */
    const cellLabel = (id: number): string => cellNames[id] ?? `${t('cellAlerts.cell')} ${id}`;

    /* LT-3320 — the sensor a tag-scoped alert is about, named `<equipment alias> [component
       alias] <sensor alias>` from its cell's equipment tree (see utils/alertSensor). */
    const sensorFor = (a: CellAlert): AlertSensor | null =>
        resolveAlertSensor(a, tagCatalogue?.get(a.facility_line_id));
    const sensorName = (sensor: AlertSensor): string =>
        sensor.label ?? t('cellAlerts.sensor.tagFallback', { id: sensor.tagId });

    // The PLC's name for the signal, for whoever needs to find it by tag. Ids and units are
    // deliberately left to Signal Data, which the click opens.
    const sensorTip = (sensor: AlertSensor): React.ReactNode => (
        <div className="alertSensorTip">
            {sensor.tag ? <div className="alertSensorTip__tag">{sensor.tag}</div> : null}
            <div className="alertSensorTip__hint">{t('cellAlerts.sensor.openHint')}</div>
        </div>
    );

    /* The drawer's message cell: the raw tag the producer leads with is swapped for the
       sensor's name (hover for the raw tag); any other message keeps its text, with the name
       in front. While the cell's catalogue is still loading the row reads exactly as before. */
    const renderMessage = (a: CellAlert): React.ReactNode => {
        const sensor = sensorFor(a);
        if (!sensor || sensor.pending) return a.message;
        const rest = messageAfterTag(a.message, sensor.tag);
        return (
            <>
                <Tooltip title={sensorTip(sensor)} placement="topLeft">
                    <span className="alertFooterDrawer__sensor">{sensorName(sensor)}</span>
                </Tooltip>
                {rest !== null ? `: ${rest}` : ` · ${a.message}`}
            </>
        );
    };

    /* Every row goes somewhere (LT-3320): to its line's Signal Data, scrolled to and
       highlighting the sensor when the alert names one. The line is selected first, exactly
       as the dashboard tiles do, because Signal Data reads the line from the selection. */
    const openAlert = (a: CellAlert) => {
        const cell = cellList?.find((c: any) => c?.id === a.facility_line_id);
        if (cell && cell.id !== selectedCellId) dispatch(userSelectionActions.setUserSelection({ cell }));
        markSeen();
        setExpanded(false);
        const sensor = sensorFor(a);
        // A cell missing from the list (stale ring-buffer entry) cannot be selected, so its
        // sensor cannot be focused either — it still opens Signal Data rather than doing nothing.
        const focus: SignalFocusRequest | undefined =
            cell && sensor
                ? { focusTagId: sensor.tagId, focusCellId: cell.id, focusLabel: sensorName(sensor) }
                : undefined;
        navigate(SIGNAL_DATA_PATH, focus ? { state: focus } : undefined);
    };

    // Base visibility = hard role gate only (controls/internal need the role).
    // Unknown audiences pass through here so their chip can appear (default
    // off via activeFilter) — filtering fully by visibleAudiences at this
    // stage made unknown-audience alerts unreachable: no row AND no chip.
    const roleVisible = useMemo(
        () => alerts.filter((a) => !isAlertRoleBlocked(a, visibleAudiences)),
        [alerts, visibleAudiences],
    );

    // Audience chips = role-visible defaults + any extra values present in the data (open set)
    const audienceOptions = useMemo(() => {
        const present = new Set<string>(visibleAudiences);
        roleVisible.forEach((a) => (a.audience ?? []).forEach((aud) => present.add(aud)));
        return Array.from(present);
    }, [roleVisible, visibleAudiences]);

    const activeFilter = audienceFilter ?? visibleAudiences;
    const audienceVisible = useMemo(
        () => roleVisible.filter((a) => isAlertVisible(a, activeFilter)),
        [roleVisible, activeFilter],
    );
    // Line scope composes with the audience chips: both apply. Everything the bar
    // shows — drawer rows, active counters, unseen badge, ticker, quiet state —
    // derives from this one list, so the badge can never disagree with the drawer.
    const visible = useMemo(
        () =>
            audienceVisible
                .filter((a) => !scoped || a.facility_line_id === selectedCellId)
                // in-place cleared-folding keeps list positions — re-sort for display
                .sort((a, b) => b.time_stamp - a.time_stamp),
        [audienceVisible, scoped, selectedCellId],
    );

    /* Scoping the alarm line means an uncleared fault on ANOTHER line goes
       invisible here — the exact failure the ticket is about, just moved. So the
       "All lines" chip carries the count of still-raised alerts the scope is
       hiding; nothing is silently dropped. */
    const hiddenByScope = useMemo(
        () =>
            scoped
                ? audienceVisible.filter((a) => a.facility_line_id !== selectedCellId && a.state === 'raised')
                      .length
                : 0,
        [audienceVisible, scoped, selectedCellId],
    );

    const active = visible.filter((a) => a.state === 'raised');
    const activeCounts = {
        critical: active.filter((a) => a.severity === 'critical').length,
        warning: active.filter((a) => a.severity === 'warning').length,
        info: active.filter((a) => a.severity === 'info').length,
    };
    // Bar chips = NEW (unseen, non-cleared) since the drawer was last opened.
    // Compared on received_ms (browser arrival time) — producer time_stamps
    // can be clock-skewed and would mint alerts that arrive already "seen".
    // Reminders re-arrive, so a still-open condition re-counts after its mute
    // window — by design (it re-notified).
    const seen = seenTs[facilityId] ?? 0;
    const unseen = visible.filter((a) => a.state !== 'cleared' && a.received_ms > seen);
    const counts = {
        critical: unseen.filter((a) => a.severity === 'critical').length,
        warning: unseen.filter((a) => a.severity === 'warning').length,
        info: unseen.filter((a) => a.severity === 'info').length,
    };
    const latest: CellAlert | undefined = unseen[0] ?? active[0] ?? visible[0];
    // The bar stays visually hot while a condition is STILL RAISED, even if seen —
    // an ongoing fault must not go quiet because someone looked at it.
    const quiet = active.length === 0 && unseen.length === 0;

    const chooseScope = (next: AlertScope) => {
        setScopeOverride(next);
        try {
            sessionStorage.setItem(SCOPE_STORAGE_KEY, next);
        } catch {
            /* storage unavailable — the choice still applies for this mount */
        }
    };

    const toggleAudience = (aud: string, checked: boolean) => {
        const next = checked ? [...activeFilter, aud] : activeFilter.filter((a) => a !== aud);
        setAudienceFilter(next);
    };

    if (!facilityId) {
        return <div className="alertFooterBar alertFooterBar--idle" data-testid="alert-footer-bar" />;
    }

    return (
        <ConfigProviderComponent theme={antdTheme}>
            <div
                className={`alertFooterBar ${quiet ? 'alertFooterBar--quiet' : 'alertFooterBar--active'}`}
                data-testid="alert-footer-bar"
                role="button"
                tabIndex={0}
                aria-label={t('cellAlerts.openFeed')}
                onClick={() => {
                    markSeen();
                    setExpanded(true);
                }}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        markSeen();
                        setExpanded(true);
                    }
                }}
            >
                {/* The bar ALWAYS declares its scope. A scoped badge with no visible
                    reason reads as an undercount, and a silent bar leaves the operator
                    guessing which of the two states they are in — so "All lines" is
                    stated just as explicitly as "<line> only". Distinct i18n key from
                    the drawer chip: same words, but the two are separate controls. */}
                <Tooltip
                    title={scoped ? t('cellAlerts.scope.barTip') : t('cellAlerts.scope.barTipAll')}
                >
                    <span
                        className={`alertFooterBar__scopeTag ${
                            scoped ? '' : 'alertFooterBar__scopeTag--all'
                        }`}
                    >
                        {scoped
                            ? t('cellAlerts.scope.barLabel', {
                                  line: cellLabel(selectedCellId as number),
                                  interpolation: { escapeValue: false },
                              })
                            : t('cellAlerts.scope.barLabelAll')}
                    </span>
                </Tooltip>
                {/* The scoped bar must never claim the facility is quiet. Without this
                    the alarm line reads "Line 5 only · 0 0 0 · No active alerts" while
                    another line sits on 19 raised warnings — the very failure this
                    feature exists to prevent, just moved one page over. The drawer's
                    +N is no help here: you have to already be looking to see it. */}
                {scoped && hiddenByScope > 0 ? (
                    <Tooltip title={t('cellAlerts.scope.barHiddenTip', { n: hiddenByScope })}>
                        <span className="alertFooterBar__scopeHidden">+{hiddenByScope}</span>
                    </Tooltip>
                ) : null}
                <span className="alertFooterBar__counts">
                    {(['critical', 'warning', 'info'] as const).map((sev) => (
                        <Tooltip key={sev} title={t(`cellAlerts.tooltipNew_${sev}`)}>
                            <span
                                className={`alertFooterBar__count alertFooterBar__count--${sev} ${
                                    counts[sev] > 0 ? 'alertFooterBar__count--nonzero' : ''
                                }`}
                            >
                                {counts[sev]}
                            </span>
                        </Tooltip>
                    ))}
                </span>
                <span className="alertFooterBar__ticker">
                    {latest ? (
                        <>
                            {alertIcon(latest)}
                            <span
                                className={`alertFooterBar__tickerText ${
                                    latest.state === 'cleared' ? 'alertFooterBar__tickerText--cleared' : ''
                                }`}
                            >
                                {formatTime(latest.time_stamp)} · {cellLabel(latest.facility_line_id)} ·{' '}
                                {latest.code}
                                {/* the ticker has no state column, so a cleared
                                    condition must say so inline (LT-3110) */}
                                {latest.state === 'cleared' ? ` (${t('cellAlerts.stateCleared')})` : ''} ·{' '}
                                {formatAlertMessage(latest, sensorFor(latest))}
                            </span>
                        </>
                    ) : (
                        <span className="alertFooterBar__tickerText alertFooterBar__tickerText--muted">
                            {scoped
                                ? t('cellAlerts.noAlertsOnLine', {
                                      line: cellLabel(selectedCellId as number),
                                      interpolation: { escapeValue: false },
                                  })
                                : t('cellAlerts.noAlerts')}
                        </span>
                    )}
                </span>
                <span className="alertFooterBar__copyright">
                    Copyright&#169; {new Date().getFullYear()} Liveline Technologies
                </span>
                <span className="alertFooterBar__expand">{expanded ? <DownOutlined /> : <UpOutlined />}</span>
            </div>

            <Drawer
                placement="bottom"
                height="45vh"
                open={expanded}
                onClose={() => {
                    markSeen(); // alerts that streamed in while open are seen too
                    setExpanded(false);
                }}
                className="alertFooterDrawer"
                title={
                    <div className="alertFooterDrawer__header">
                        <span>
                            {t('cellAlerts.title')}
                            <span className="alertFooterDrawer__activeSummary">
                                {(['critical', 'warning', 'info'] as const).map((sev) => (
                                    <Tooltip key={sev} title={t(`cellAlerts.tooltipActive_${sev}`)}>
                                        <span
                                            className={`alertFooterBar__count alertFooterBar__count--${sev} ${
                                                activeCounts[sev] > 0 ? 'alertFooterBar__count--nonzero' : ''
                                            }`}
                                        >
                                            {activeCounts[sev]}
                                        </span>
                                    </Tooltip>
                                ))}
                            </span>
                        </span>
                        <span className="alertFooterDrawer__filters">
                            {selectedCellId && !facilityWidePage ? (
                                <span className="alertFooterDrawer__scope">
                                    <Tooltip
                                        title={t('cellAlerts.scope.thisLineTip', {
                                            line: cellLabel(selectedCellId),
                                            interpolation: { escapeValue: false },
                                        })}
                                    >
                                        <CheckableTag
                                            checked={scope === 'line'}
                                            onChange={() => chooseScope('line')}
                                        >
                                            {t('cellAlerts.scope.thisLine')}
                                        </CheckableTag>
                                    </Tooltip>
                                    <Tooltip
                                        title={
                                            hiddenByScope > 0
                                                ? t('cellAlerts.scope.hiddenTip', { n: hiddenByScope })
                                                : t('cellAlerts.scope.allLinesTip')
                                        }
                                    >
                                        <CheckableTag
                                            checked={scope === 'all'}
                                            onChange={() => chooseScope('all')}
                                        >
                                            {t('cellAlerts.scope.allLines')}
                                            {hiddenByScope > 0 ? (
                                                <span className="alertFooterDrawer__scopeHidden">
                                                    +{hiddenByScope}
                                                </span>
                                            ) : null}
                                        </CheckableTag>
                                    </Tooltip>
                                </span>
                            ) : null}
                            <span className="alertFooterDrawer__chips">
                                {audienceOptions.map((aud) => (
                                    <CheckableTag
                                        key={aud}
                                        checked={activeFilter.includes(aud)}
                                        onChange={(checked) => toggleAudience(aud, checked)}
                                    >
                                        {aud}
                                    </CheckableTag>
                                ))}
                            </span>
                        </span>
                    </div>
                }
            >
                {visible.length === 0 ? (
                    <Empty
                        description={
                            scoped
                                ? t('cellAlerts.noAlertsOnLine', {
                                      line: cellLabel(selectedCellId as number),
                                      interpolation: { escapeValue: false },
                                  })
                                : t('cellAlerts.noAlerts')
                        }
                    />
                ) : (
                    <div className="alertFooterDrawer__list">
                        {visible.map((a) => (
                            <div
                                key={a.alert_id}
                                className={`alertFooterDrawer__row alertFooterDrawer__row--${a.severity} alertFooterDrawer__row--state-${a.state}`}
                                role="link"
                                tabIndex={0}
                                onClick={() => openAlert(a)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        openAlert(a);
                                    }
                                }}
                            >
                                <span className="alertFooterDrawer__icon">{alertIcon(a)}</span>
                                <span className="alertFooterDrawer__time">{formatTime(a.time_stamp)}</span>
                                <span className="alertFooterDrawer__cell">{cellLabel(a.facility_line_id)}</span>
                                <span className="alertFooterDrawer__code">{a.code}</span>
                                <span className="alertFooterDrawer__message">{renderMessage(a)}</span>
                                {/* the badge owns a grid column of its own, so an
                                    unfolded row must still fill that cell — otherwise
                                    the state pill slides into it and wraps (LT-3193) */}
                                {(a.repeat_count ?? 1) > 1 ? (
                                    <Tooltip
                                        title={`${t('cellAlerts.repeatFolded')}: ${a.repeat_count} · ${formatTime(
                                            a.first_time_stamp ?? a.time_stamp,
                                        )} → ${formatTime(a.time_stamp)}`}
                                    >
                                        <span className="alertFooterDrawer__repeat">×{a.repeat_count}</span>
                                    </Tooltip>
                                ) : (
                                    <span aria-hidden="true" />
                                )}
                                <span className={`alertFooterDrawer__state alertFooterDrawer__state--${a.state}`}>
                                    {a.state === 'raised'
                                        ? t('cellAlerts.stateActive')
                                        : a.state === 'cleared'
                                        ? t('cellAlerts.stateCleared')
                                        : t('cellAlerts.stateEvent')}
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </Drawer>
        </ConfigProviderComponent>
    );
};

export default AlertFooterBar;
